import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { ReportHeader, StatCard } from '@/components/reports/ReportUI';
import { ReportFilterBar } from '@/components/reports/ReportFilterBar';
import { ExportButtons } from '@/components/ExportButtons';;
import { ViewSourceLink } from '@/components/accounting/ViewSourceLink';
import { sourceTypeLabel } from '@/lib/sourceNav';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { BookOpen, Plus, Trash2, Search, Check, X, ArrowLeft, Save, AlertTriangle, Copy, ArrowRightLeft, Edit2, Rows3, Table as TableIcon } from 'lucide-react';
import api from '@/lib/api';
import { getTaxRates } from '@/lib/configCache';
import { useLanguage } from '@/contexts/LanguageContext';
import { localToday } from '@/lib/dateRange';

const TYPE_BADGES = {
  manual: 'bg-muted text-muted-foreground',
  adjustment: 'bg-warning/20 text-warning',
  closing: 'bg-error/20 text-error',
  opening: 'bg-info/20 text-info',
  auto_generated: 'bg-success/20 text-success',
  year_end_closing: 'bg-error/20 text-error',
};

const STATIC_TAX_OPTIONS = [
  { value: 'no_tax', label: 'No Taxes', rate: 0 },
  { value: 'exempt', label: 'Exempt', rate: 0 },
  { value: 'zero_rated', label: 'Zero Rated', rate: 0 },
];

// Display-only: tolerate stored dates like "2026-10-25 14:00:00", "…+00:00Z" or "25/10/2026" (stored value never changed)
function fmtDate(v, fallback) {
  const raw = v || fallback;
  if (!raw) return '-';
  const str = String(raw).trim();
  let d = new Date(str);
  if (isNaN(d)) d = new Date(str.replace(' ', 'T').replace(/\+00:00Z$/, 'Z'));
  if (isNaN(d)) {
    const m = str.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
    if (m) d = new Date(`${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}T00:00:00`);
  }
  if (isNaN(d)) { const iso = str.match(/\d{4}-\d{2}-\d{2}/); return iso ? iso[0] : str.slice(0, 10); }
  return d.toLocaleDateString();
}

function emptyLine() {
  return { account_code: '', account_name: '', debit: 0, credit: 0, description: '', memo: '', cost_center: '', tax_status: 'no_tax', tax_rate: 0, tax_amount: 0 };
}

export default function JournalEntriesPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ total_debit: 0, total_credit: 0, is_balanced: true });
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: "all" });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);

  const [form, setForm] = useState({
    date: localToday(),
    description: '', reference: '', entry_type: 'manual', memo: '',
    lines: [emptyLine(), emptyLine()],
  });

  const [includeAuto, setIncludeAuto] = useState(() => localStorage.getItem('je_include_auto') === 'true');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('je_view_mode') || 'compact'); // 'compact' | 'ledger'
  const [autoCount, setAutoCount] = useState(0);
  // Advanced filters
  const [branchFilter, setBranchFilter] = useState([]);
  const [filterEntryNumber, setFilterEntryNumber] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');

  const fetchEntries = async () => {
    try {
      const params = { page, limit: 50, include_auto: includeAuto };
      if (search) params.search = search;
      if (branchFilter.length === 1) params.branch_id = branchFilter[0];
      else if (branchFilter.length > 1) params.branch_ids = branchFilter.join(',');
      if (filterEntryNumber) params.entry_number = filterEntryNumber;
      if (filterAccount) params.account = filterAccount;
      if (filterMinAmount) params.min_amount = filterMinAmount;
      if (filterMaxAmount) params.max_amount = filterMaxAmount;
      if (dateFilter?.start) params.start_date = dateFilter.start;
      if (dateFilter?.end) params.end_date = dateFilter.end;
      const res = await api.get('/accounting/journal-entries', { params });
      setEntries(res.data.entries);
      setTotal(res.data.total);
      setPages(res.data.pages);
      setAutoCount(res.data.auto_count || 0);
      setTotals(res.data.totals || { total_debit: 0, total_credit: 0, is_balanced: true });
    } catch { toast.error('Failed to load journal entries'); }
  };

  const fetchAccounts = async () => {
    try { const res = await api.get('/accounting/accounts'); setAccounts(res.data); } catch {}
  };

  const fetchBranches = async () => {
    try { const res = await api.get('/branches'); setBranches([...(res.data || []), { id: '__unassigned__', name: 'Unassigned (no branch)' }]); } catch {}
  };

  const fetchTaxRates = async () => {
    try { const data = await getTaxRates(); setTaxRates(data); } catch {}
  };

  useEffect(() => { fetchEntries(); fetchAccounts(); fetchBranches(); fetchTaxRates(); }, []);
  useEffect(() => { fetchEntries(); /* eslint-disable-next-line */ }, [page, includeAuto, branchFilter, filterEntryNumber, filterAccount, filterMinAmount, filterMaxAmount, dateFilter?.start, dateFilter?.end, search]);
  useEffect(() => { localStorage.setItem('je_include_auto', String(includeAuto)); }, [includeAuto]);
  useEffect(() => { localStorage.setItem('je_view_mode', viewMode); }, [viewMode]);

  // Deep-link support — auto-apply filter state from URL query params.
  useEffect(() => {
    const qpEntry = searchParams.get('entry_number');
    const qpAccount = searchParams.get('account');
    const qpStart = searchParams.get('start_date');
    const qpEnd = searchParams.get('end_date');
    if (qpEntry && qpEntry !== filterEntryNumber) setFilterEntryNumber(qpEntry);
    if (qpAccount && qpAccount !== filterAccount) setFilterAccount(qpAccount);
    if (qpStart || qpEnd) {
      setDateFilter(f => ({
        ...f,
        start: qpStart || f?.start || null,
        end: qpEnd || f?.end || null,
        period: 'custom',
      }));
    }
    // Drill-down always wants to include auto-generated entries so users find their matches.
    if (qpEntry || qpAccount) setIncludeAuto(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Group accounts by type
  const accountsByType = accounts.reduce((acc, a) => {
    const type = a.type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(a);
    return acc;
  }, {});

  // Build tax options from API + static
  const taxOptions = [
    ...STATIC_TAX_OPTIONS,
    ...taxRates.map(tr => ({ value: `tax_${tr.id}`, label: `${tr.name} (${tr.rate}%)`, rate: tr.rate })),
  ];

  const totalDebit = form.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const totalDiff = Math.abs(totalDebit - totalCredit);
  const isBalanced = totalDiff < 0.01 && totalDebit > 0;

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => { if (form.lines.length > 2) setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) })); };
  const clearAll = () => setForm(f => ({ ...f, lines: [emptyLine(), emptyLine()] }));

  const updateLine = (i, field, val) => {
    const lines = [...form.lines];
    if (field === 'account_code') {
      const acc = accounts.find(a => a.code === val);
      lines[i] = { ...lines[i], account_code: val, account_name: acc?.name || '' };
    } else if (field === 'debit' || field === 'credit') {
      lines[i] = { ...lines[i], [field]: parseFloat(val) || 0 };
      if (field === 'debit' && parseFloat(val) > 0) lines[i].credit = 0;
      if (field === 'credit' && parseFloat(val) > 0) lines[i].debit = 0;
    } else if (field === 'tax_status') {
      lines[i] = { ...lines[i], tax_status: val };
      const opt = taxOptions.find(o => o.value === val);
      const rate = opt ? opt.rate : 0;
      const baseAmount = (parseFloat(lines[i].debit) || 0) + (parseFloat(lines[i].credit) || 0);
      lines[i].tax_rate = rate;
      lines[i].tax_amount = rate > 0 ? +(baseAmount * rate / 100).toFixed(2) : 0;
    } else {
      lines[i] = { ...lines[i], [field]: val };
    }
    setForm(f => ({ ...f, lines }));
  };

  const handleSave = async () => {
    if (!form.description && !form.memo) { toast.error('Description or Memo is required'); return; }
    if (!isBalanced) { toast.error('Debits must equal Credits'); return; }
    const payload = {
      ...form,
      description: form.description || form.memo,
      date: new Date(form.date).toISOString(),
      lines: form.lines.filter(l => l.account_code).map(l => ({
        account_code: l.account_code,
        account_name: l.account_name,
        debit: l.debit || 0,
        credit: l.credit || 0,
        memo: l.description || l.memo || '',
        cost_center: l.cost_center || '',
        tax_status: l.tax_status || 'no_tax',
        tax_amount: l.tax_amount || 0,
      })),
    };
    try {
      if (editingId) {
        await api.put(`/accounting/journal-entries/${editingId}`, payload);
        toast.success('Journal entry updated');
      } else {
        await api.post('/accounting/journal-entries', payload);
        toast.success('Journal entry saved');
      }
      setShowCreate(false);
      setEditingId(null);
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this journal entry?')) return;
    try { await api.delete(`/accounting/journal-entries/${id}`); toast.success('Deleted'); fetchEntries(); }
    catch { toast.error('Failed to delete'); }
  };

  const openEdit = (entry) => {
    setForm({
      date: entry.date?.split('T')[0] || '',
      description: entry.description || '',
      reference: entry.reference || '',
      entry_type: entry.entry_type || 'manual',
      memo: entry.memo || entry.notes || '',
      lines: (entry.lines || []).map(l => ({
        account_code: l.account_code || '',
        account_name: l.account_name || '',
        debit: l.debit || 0,
        credit: l.credit || 0,
        description: l.description || '',
        memo: l.memo || '',
        cost_center: l.cost_center || '',
        tax_status: l.tax_status || 'no_tax',
        tax_rate: l.tax_rate || 0,
        tax_amount: l.tax_amount || 0,
      })),
    });
    setEditingId(entry.id);
    setShowCreate(true);
  };

  const [editingId, setEditingId] = useState(null);

  const openCreate = () => {
    setForm({
      date: localToday(),
      description: '', reference: '', entry_type: 'manual', memo: '',
      lines: [emptyLine(), emptyLine()],
    });
    setEditingId(null);
    setShowCreate(true);
  };

  const filtered = entries.filter(e =>
    !search || e.description?.toLowerCase().includes(search.toLowerCase()) || e.entry_number?.toLowerCase().includes(search.toLowerCase())
  );

  // ── CREATE VIEW (Full-page) ──
  if (showCreate) {
    return (
      <DashboardLayout>
        <div className="space-y-5 max-w-[1400px] mx-auto" data-testid="journal-create-page">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)} className="rounded-xl" data-testid="back-to-list-btn">
                <ArrowLeft size={16} className="mr-1" /> Back
              </Button>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                <BookOpen size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold font-outfit text-foreground">{editingId ? 'Edit Journal Entry' : 'Create Journal Entry'}</h1>
                <p className="text-xs text-muted-foreground">Manual debit and credit entry</p>
              </div>
            </div>
            <Button onClick={handleSave} disabled={!isBalanced} className="rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 gap-2" data-testid="save-entry-btn">
              <Save size={15} /> Save Entry
            </Button>
          </div>

          {/* VAT Warning */}
          <div className="flex items-center gap-3 px-5 py-3 bg-warning/10 border border-warning/30 rounded-xl text-warning text-sm" data-testid="vat-warning">
            <AlertTriangle size={18} className="shrink-0 text-warning" />
            <p>Please be aware that including both invoices VAT and returns VAT in the same journal entry will impact your VAT reports.</p>
          </div>

          {/* Date & Reference Row */}
          <div className="flex items-end gap-6">
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Journal Date</label>
              <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-52 rounded-lg h-10" data-testid="entry-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Entry Type</label>
              <Select value={form.entry_type} onValueChange={v => setForm({...form, entry_type: v})}>
                <SelectTrigger className="w-40 rounded-lg h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="adjustment">Adjustment</SelectItem>
                  <SelectItem value="closing">Closing</SelectItem>
                  <SelectItem value="opening">Opening</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Reference</label>
              <Input value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} placeholder="e.g. INV-001" className="w-44 rounded-lg h-10" data-testid="entry-reference" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Description</label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Entry description" className="w-72 rounded-lg h-10" data-testid="entry-description" />
            </div>
          </div>

          {/* Entry Lines Table */}
          <Card className="border-border/60 rounded-xl overflow-hidden shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1100px]">
                  <thead className="bg-muted/50 border-b border-border/60">
                    <tr>
                      <th className="w-10 px-2 py-3 text-center text-xs font-bold text-muted-foreground uppercase">#</th>
                      <th className="px-2 py-3 text-left text-xs font-bold text-muted-foreground uppercase w-[220px]">Account</th>
                      <th className="px-2 py-3 text-right text-xs font-bold text-muted-foreground uppercase w-[110px]">Debit</th>
                      <th className="px-2 py-3 text-right text-xs font-bold text-muted-foreground uppercase w-[110px]">Credit</th>
                      <th className="px-2 py-3 text-left text-xs font-bold text-muted-foreground uppercase w-[180px]">Description</th>
                      <th className="px-2 py-3 text-left text-xs font-bold text-muted-foreground uppercase w-[140px]">Cost Center</th>
                      <th className="px-2 py-3 text-left text-xs font-bold text-muted-foreground uppercase w-[130px]">Tax Status</th>
                      <th className="px-2 py-3 text-right text-xs font-bold text-muted-foreground uppercase w-[90px]">Tax</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((line, i) => (
                      <tr key={i} className="border-t border-border/60 hover:bg-muted/50 transition-colors" data-testid={`journal-line-${i}`}>
                        <td className="px-2 py-2 text-center">
                          <span className="text-xs font-mono text-muted-foreground">{i + 1}</span>
                        </td>
                        <td className="px-2 py-2">
                          <Select value={line.account_code || "none"} onValueChange={v => updateLine(i, 'account_code', v === "none" ? "" : v)}>
                            <SelectTrigger className="text-xs h-9 rounded-lg border-border" data-testid={`line-account-${i}`}>
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              <SelectItem value="none">Select account</SelectItem>
                              {['asset', 'liability', 'equity', 'revenue', 'expense'].map(type => (
                                accountsByType[type] && accountsByType[type].length > 0 && (
                                  <div key={type}>
                                    <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/50 sticky top-0">{type}</div>
                                    {accountsByType[type].map(a => (
                                      <SelectItem key={a.code} value={a.code} className="text-xs">{a.code} - {a.name}</SelectItem>
                                    ))}
                                  </div>
                                )
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          <Input className="text-xs text-right h-9 rounded-lg border-border font-mono" type="number" step="0.01" value={line.debit || ''} onChange={e => updateLine(i, 'debit', e.target.value)} placeholder="0.00" data-testid={`line-debit-${i}`} />
                        </td>
                        <td className="px-2 py-2">
                          <Input className="text-xs text-right h-9 rounded-lg border-border font-mono" type="number" step="0.01" value={line.credit || ''} onChange={e => updateLine(i, 'credit', e.target.value)} placeholder="0.00" data-testid={`line-credit-${i}`} />
                        </td>
                        <td className="px-2 py-2">
                          <Textarea className="text-xs h-9 min-h-[36px] rounded-lg border-border resize-none py-2" value={line.description} onChange={e => updateLine(i, 'description', e.target.value)} placeholder="Line description..." />
                        </td>
                        <td className="px-2 py-2">
                          <Select value={line.cost_center || "none"} onValueChange={v => updateLine(i, 'cost_center', v === "none" ? "" : v)}>
                            <SelectTrigger className="text-xs h-9 rounded-lg border-border" data-testid={`line-cost-center-${i}`}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Center</SelectItem>
                              <SelectItem value="main">Main Center</SelectItem>
                              {branches.map(b => (
                                <SelectItem key={b.id} value={b.id} className="text-xs">{b.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          <Select value={line.tax_status || "no_tax"} onValueChange={v => updateLine(i, 'tax_status', v)}>
                            <SelectTrigger className="text-xs h-9 rounded-lg border-border" data-testid={`line-tax-status-${i}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {taxOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2 text-right">
                          <span className="text-xs font-mono text-muted-foreground">{line.tax_amount > 0 ? line.tax_amount.toFixed(2) : '-'}</span>
                        </td>
                        <td className="px-1 py-2">
                          {form.lines.length > 2 && (
                            <button onClick={() => removeLine(i)} className="p-1.5 rounded-lg hover:bg-error/10 transition-colors" data-testid={`remove-line-${i}`}>
                              <X className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border/80 bg-muted/50">
                      <td className="px-2 py-3"></td>
                      <td className="px-2 py-3 text-sm font-bold text-foreground">Total</td>
                      <td className="px-2 py-3 text-right font-bold text-success font-mono text-sm">{totalDebit.toFixed(2)}</td>
                      <td className="px-2 py-3 text-right font-bold text-info font-mono text-sm">{totalCredit.toFixed(2)}</td>
                      <td colSpan={4} className="px-2 py-3"></td>
                      <td></td>
                    </tr>
                    <tr className="bg-muted/50">
                      <td className="px-2 py-2"></td>
                      <td className="px-2 py-2 text-xs font-semibold text-muted-foreground">Total Difference</td>
                      <td colSpan={2} className="px-2 py-2 text-center">
                        {isBalanced ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-success/10 text-success text-xs font-semibold">
                            <Check size={12} /> Balanced
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-error/10 text-error text-xs font-semibold">
                            {totalDiff.toFixed(2)} difference
                          </span>
                        )}
                      </td>
                      <td colSpan={4}></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={addLine} className="rounded-xl gap-1.5" data-testid="add-line-btn">
              <Plus size={14} /> Add new record
            </Button>
            <Button variant="ghost" size="sm" onClick={clearAll} className="rounded-xl gap-1.5 text-muted-foreground hover:text-error" data-testid="clear-all-btn">
              <Trash2 size={14} /> Clear all
            </Button>
          </div>

          {/* Memo */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Memo</label>
            <Textarea
              value={form.memo}
              onChange={e => setForm({...form, memo: e.target.value})}
              placeholder="General notes for this journal entry..."
              className="min-h-[100px] rounded-xl border-border"
              data-testid="entry-memo"
            />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── LIST VIEW ──
  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="journal-entries-page">
        <ReportHeader icon={BookOpen} title="Journal Entries" subtitle={t('journal_subtitle') || 'Manual debit and credit entries'} testId="je-header"
          actions={<>
            <label
              className="flex items-center gap-2 px-3 h-9 rounded-xl border border-border bg-card text-xs cursor-pointer hover:border-primary/40 transition-colors"
              title="When ON, bills, expenses, supplier payments and salary payments are synthesized as read-only journal entries and merged into this list. Manual entries are always shown."
              data-testid="include-auto-toggle-label"
            >
              <input
                type="checkbox"
                checked={includeAuto}
                onChange={(e) => setIncludeAuto(e.target.checked)}
                className="accent-primary"
                data-testid="include-auto-toggle"
              />
              <span className="font-medium">Include Bills/Expenses/Salaries</span>
              {includeAuto && autoCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold">+{autoCount}</span>
              )}
            </label>
            <div className="flex rounded-xl border border-border bg-card overflow-hidden" data-testid="view-mode-toggle">
              <button
                onClick={() => setViewMode('compact')}
                className={`px-2.5 h-9 text-xs flex items-center gap-1 transition-colors ${viewMode === 'compact' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted'}`}
                title="One row per entry"
                data-testid="view-compact"
              ><TableIcon size={13} /> Compact</button>
              <button
                onClick={() => setViewMode('ledger')}
                className={`px-2.5 h-9 text-xs flex items-center gap-1 transition-colors ${viewMode === 'ledger' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted'}`}
                title="Show every debit/credit leg"
                data-testid="view-ledger"
              ><Rows3 size={13} /> Ledger</button>
            </div>
            <Button onClick={openCreate} data-testid="add-entry-btn" className="rounded-xl">
              <Plus className="w-4 h-4 mr-1.5" /> New Entry
            </Button>
          </>} />
        <ReportFilterBar testId="je-filter" branches={branches} branchIds={branchFilter} onBranchIds={setBranchFilter} allowAll defaultPreset="all"
          range={dateFilter} onRange={setDateFilter}>
          <ExportButtons dataType="journal_entries" label="Journal Entries" startDate={dateFilter?.start} endDate={dateFilter?.end} branchIds={branchFilter} />
        </ReportFilterBar>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard label="Total Entries" value={total} tone="slate" testId="summary-total" />
          <StatCard label="Total Debits" value={`${cur()} ${totals.total_debit.toLocaleString()}`} tone="emerald" testId="summary-debit"
            sub={branchFilter.length ? 'lines of the selected branches' : undefined} />
          <StatCard label="Total Credits" value={`${cur()} ${totals.total_credit.toLocaleString()}`} tone="blue" testId="summary-credit"
            sub={branchFilter.length ? 'branch lines need not balance (inter-branch legs)' : undefined} />
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search entries by description or number..." value={search} onChange={e => setSearch(e.target.value)} className="pl-11 h-11 rounded-xl border-border/60 bg-white shadow-sm" data-testid="search-entries" />
        </div>

        {/* Advanced Filters */}
        <Card className="border-border/60 bg-white">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mr-1">Filters</span>
            <Input
              placeholder="Entry #  e.g. JE-0025"
              value={filterEntryNumber}
              onChange={e => setFilterEntryNumber(e.target.value)}
              className="h-9 w-[170px] text-xs"
              data-testid="filter-entry-number"
            />
            <Input
              placeholder="Account (code or name)"
              value={filterAccount}
              onChange={e => setFilterAccount(e.target.value)}
              className="h-9 w-[200px] text-xs"
              data-testid="filter-account"
            />
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Amount:</span>
              <Input
                type="number" placeholder="min" value={filterMinAmount}
                onChange={e => setFilterMinAmount(e.target.value)}
                className="h-9 w-[80px] text-xs" data-testid="filter-min"
              />
              <span>–</span>
              <Input
                type="number" placeholder="max" value={filterMaxAmount}
                onChange={e => setFilterMaxAmount(e.target.value)}
                className="h-9 w-[80px] text-xs" data-testid="filter-max"
              />
            </div>
            {(branchFilter.length > 0 || filterEntryNumber || filterAccount || filterMinAmount || filterMaxAmount) && (
              <Button
                variant="ghost" size="sm" className="h-9 text-xs text-muted-foreground"
                onClick={() => { setBranchFilter([]); setFilterEntryNumber(''); setFilterAccount(''); setFilterMinAmount(''); setFilterMaxAmount(''); }}
                data-testid="clear-filters-btn"
              >
                <X size={12} className="mr-1" /> Clear
              </Button>
            )}
            {(dateFilter?.start || dateFilter?.end) && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 h-9 rounded-lg border border-primary/30 bg-primary/5 text-[11px] text-primary font-medium"
                data-testid="je-date-chip"
                title="Date filter applied from URL"
              >
                {dateFilter?.start || '…'} → {dateFilter?.end || '…'}
                <button
                  onClick={() => setDateFilter({ start: null, end: null, period: 'all' })}
                  className="hover:bg-primary/10 rounded p-0.5"
                  data-testid="je-date-chip-clear"
                  title="Clear date filter"
                >
                  <X size={11} />
                </button>
              </span>
            )}
            <span className="ml-auto text-[10px] text-muted-foreground">
              <strong className="text-foreground">{total}</strong> entries match
            </span>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-border/60 overflow-hidden rounded-xl shadow-sm">
          <CardContent className="p-0">
            {viewMode === 'ledger' ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border/60">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider w-[110px]">Entry #</th>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider w-[100px]">Date</th>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Account / Description</th>
                      <th className="text-right px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider w-[130px]">Debit</th>
                      <th className="text-right px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider w-[130px]">Credit</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-16 text-muted-foreground">
                        <BookOpen size={36} className="mx-auto mb-3 opacity-20" />
                        <p className="text-sm font-medium">No journal entries yet</p>
                      </td></tr>
                    ) : filtered.flatMap((entry, entryIdx) => {
                      const lines = entry.lines || [];
                      const rows = [];
                      // Header row per entry
                      rows.push(
                        <tr key={`h-${entry.id}`} className={`${entryIdx > 0 ? 'border-t-4 border-border/40' : ''} bg-muted/30`} data-testid={`ledger-entry-${entry.id}`}>
                          <td className="px-4 py-2 font-mono text-xs font-bold text-foreground">
                            {entry.entry_number}
                            {entry.is_auto && <Badge className="ml-1.5 text-[9px] bg-muted text-muted-foreground border-0">auto</Badge>}
                            {entry.reconciled && <Badge className="ml-1.5 text-[9px] bg-success/10 text-success border-0">reconciled</Badge>}
                          </td>
                          <td className="px-4 py-2 text-xs text-muted-foreground">{fmtDate(entry.date, entry.created_at)}</td>
                          <td className="px-4 py-2 text-xs font-medium text-foreground" colSpan={2}>{entry.description}</td>
                          <td className="px-4 py-2 text-right">
                            <Badge className={`text-[10px] ${TYPE_BADGES[entry.entry_type] || TYPE_BADGES.manual}`}>{(entry.entry_type || 'manual').replace(/_/g, ' ')}</Badge>
                          </td>
                          <td className="px-4 py-2 text-right" onClick={e => e.stopPropagation()}>
                            {!entry.is_auto && (
                              <div className="flex items-center gap-1 justify-end">
                                <button onClick={() => openEdit(entry)} className="p-1 rounded hover:bg-info/10" title="Edit" data-testid={`ledger-edit-${entry.id}`}>
                                  <Edit2 className="w-3.5 h-3.5 text-info" />
                                </button>
                                <button onClick={() => handleDelete(entry.id)} className="p-1 rounded hover:bg-error/10" data-testid={`ledger-delete-${entry.id}`}>
                                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>,
                      );
                      // One row per leg
                      lines.forEach((l, i) => {
                        rows.push(
                          <tr
                            key={`l-${entry.id}-${i}`}
                            className="border-t border-border/40 hover:bg-primary/5 cursor-pointer"
                            onClick={() => {
                              const q = new URLSearchParams();
                              if (l.account_code) q.set('account', l.account_code);
                              navigate(`/general-ledger?${q.toString()}`);
                            }}
                            data-testid={`ledger-leg-${entry.id}-${i}`}
                          >
                            <td className="px-4 py-2"></td>
                            <td className="px-4 py-2"></td>
                            <td className="px-4 py-2 text-xs">
                              <span className="font-medium text-foreground">{l.account_code}</span>
                              <span className="text-muted-foreground ml-1.5">— {l.account_name}</span>
                              {l.memo && <div className="text-[10px] text-muted-foreground mt-0.5 italic">{l.memo}</div>}
                            </td>
                            <td className="px-4 py-2 text-right font-mono text-xs font-semibold text-success">
                              {l.debit > 0 ? `${cur()} ${Number(l.debit).toLocaleString()}` : ''}
                            </td>
                            <td className="px-4 py-2 text-right font-mono text-xs font-semibold text-info">
                              {l.credit > 0 ? `${cur()} ${Number(l.credit).toLocaleString()}` : ''}
                            </td>
                            <td></td>
                          </tr>,
                        );
                      });
                      // Totals strip per entry
                      rows.push(
                        <tr key={`t-${entry.id}`} className="border-t border-border/60 bg-muted/30">
                          <td colSpan={3} className="px-4 py-1.5 text-right text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Totals</td>
                          <td className="px-4 py-1.5 text-right font-mono text-xs font-bold text-success">{cur()} {Number(entry.total_debit || 0).toLocaleString()}</td>
                          <td className="px-4 py-1.5 text-right font-mono text-xs font-bold text-info">{cur()} {Number(entry.total_credit || 0).toLocaleString()}</td>
                          <td></td>
                        </tr>,
                      );
                      return rows;
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b border-border/60">
                  <tr>
                    <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Entry #</th>
                    <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Date</th>
                    <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Description</th>
                    <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Type</th>
                    <th className="text-right px-5 py-3.5 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Debit</th>
                    <th className="text-right px-5 py-3.5 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Credit</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-16 text-muted-foreground">
                      <BookOpen size={36} className="mx-auto mb-3 opacity-20" />
                      <p className="text-sm font-medium">No journal entries yet</p>
                      <p className="text-xs mt-1 text-muted-foreground">Create your first entry to get started</p>
                    </td></tr>
                  ) : filtered.map(entry => (
                    <tr key={entry.id} className="border-t border-border/60 hover:bg-muted/50 transition-colors cursor-pointer group"
                      onClick={() => setShowDetail(entry)} data-testid={`entry-row-${entry.id}`}>
                      <td className="px-5 py-3.5 font-mono text-xs text-muted-foreground">
                        {entry.entry_number}
                        {entry.is_auto && <Badge className="ml-1.5 text-[9px] bg-muted text-muted-foreground border-0" title={`Auto-generated from ${entry.source_type}`}>auto</Badge>}
                      </td>
                      <td className="px-5 py-3.5 text-muted-foreground text-xs">{fmtDate(entry.date, entry.created_at)}</td>
                      <td className="px-5 py-3.5 font-medium text-foreground max-w-[280px] truncate">{entry.description}</td>
                      <td className="px-5 py-3.5">
                        <Badge className={`text-[10px] ${TYPE_BADGES[entry.entry_type] || TYPE_BADGES.manual}`}>{(entry.entry_type || 'manual').replace(/_/g, ' ')}</Badge>
                      </td>
                      <td className="px-5 py-3.5 text-right font-mono text-xs font-semibold text-success">{cur()} {entry.total_debit?.toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-right font-mono text-xs font-semibold text-info">{cur()} {entry.total_credit?.toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end">
                          {entry.is_auto ? (
                            <span className="text-[9px] text-muted-foreground italic pr-2" title="Auto-synced from source — edit the original record instead">read-only</span>
                          ) : (<>
                            <button onClick={() => openEdit(entry)} className="p-1.5 rounded-lg hover:bg-info/10 transition-colors opacity-0 group-hover:opacity-100" data-testid={`edit-entry-${entry.id}`} title="Edit">
                              <Edit2 className="w-3.5 h-3.5 text-info" />
                            </button>
                            <button onClick={() => handleDelete(entry.id)} className="p-1.5 rounded-lg hover:bg-error/10 transition-colors opacity-0 group-hover:opacity-100" data-testid={`delete-entry-${entry.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 py-3 border-t border-border">
                {Array.from({length: pages}, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${p === page ? 'bg-indigo-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted'}`}>{p}</button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Modal */}
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-lg rounded-2xl" data-testid="entry-detail-modal">
            <DialogHeader><DialogTitle className="text-lg font-bold font-outfit">Journal Entry Details</DialogTitle></DialogHeader>
            {showDetail && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 rounded-xl bg-muted/50">
                    <p className="text-[10px] text-muted-foreground uppercase font-semibold">Entry #</p>
                    <p className="font-mono font-bold text-foreground mt-0.5">{showDetail.entry_number}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/50">
                    <p className="text-[10px] text-muted-foreground uppercase font-semibold">Date</p>
                    <p className="font-medium text-foreground mt-0.5">{fmtDate(showDetail.date, showDetail.created_at)}</p>
                  </div>
                  <div className="col-span-2 p-3 rounded-xl bg-muted/50">
                    <p className="text-[10px] text-muted-foreground uppercase font-semibold">Description</p>
                    <p className="font-medium text-foreground mt-0.5">{showDetail.description}</p>
                  </div>
                </div>
                {(showDetail.source_type || showDetail.reference) && (
                  <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border/60 bg-background">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase font-semibold">Source Document</p>
                      <p className="font-medium text-foreground mt-0.5 text-sm">
                        {sourceTypeLabel(showDetail.source_type) || 'Manual Entry'}
                        {showDetail.reference ? <span className="text-muted-foreground"> · {showDetail.reference}</span> : null}
                      </p>
                    </div>
                    <ViewSourceLink sourceType={showDetail.source_type} sourceId={showDetail.source_id} reference={showDetail.reference} />
                  </div>
                )}
                <div className="rounded-xl border border-border/60 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-muted-foreground font-semibold text-xs">Account</th>
                          <th className="text-right px-4 py-2.5 text-muted-foreground font-semibold text-xs">Debit</th>
                          <th className="text-right px-4 py-2.5 text-muted-foreground font-semibold text-xs">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(showDetail.lines || []).map((l, i) => (
                          <tr
                            key={i}
                            className="border-t border-border/60 hover:bg-primary/5 cursor-pointer group"
                            onClick={() => {
                              setShowDetail(null);
                              const q = new URLSearchParams();
                              if (l.account_code) q.set('account', l.account_code);
                              navigate(`/general-ledger?${q.toString()}`);
                            }}
                            title="Click to view this account's ledger"
                            data-testid={`je-detail-line-${i}`}
                          >
                            <td className="px-4 py-2.5 text-foreground font-medium group-hover:text-primary transition-colors">{l.account_code} - {l.account_name}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs font-semibold text-success">{l.debit > 0 ? `${cur()} ${l.debit.toFixed(2)}` : ''}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs font-semibold text-info">{l.credit > 0 ? `${cur()} ${l.credit.toFixed(2)}` : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-muted/50 border-t border-border/60">
                        <tr>
                          <td className="px-4 py-2.5 font-bold text-foreground">Total</td>
                          <td className="px-4 py-2.5 text-right font-bold text-success">{cur()} {showDetail.total_debit?.toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-info">{cur()} {showDetail.total_credit?.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
